import createOpenApiClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "./schema";

/**
 * @theta/api-client — the only code that knows how to talk to the API
 *
 * Spec §3.4. Everything here is hand-written; everything in `./schema.d.ts` is
 * generated from `backend/openapi.json` and must never be edited. The split is
 * the point: types follow the contract automatically, and the five things a
 * consumer would otherwise reimplement live in one place.
 *
 * THE FIVE THINGS THIS OWNS
 *  1. **Envelope unwrapping.** `client.me()` resolves to `Me | null`, not to
 *     `{data: Me | null}`. Collections resolve to `{items, nextCursor,
 *     hasMore}`, so React Query's `getNextPageParam` is
 *     `(last) => last.nextCursor` for every infinite list in the app.
 *  2. **Errors.** A non-2xx throws `ThetaError` with a narrowable `code`,
 *     plus `message`, `fields`, `status` and `requestId`.
 *     `isThetaError(e, "membershipRequired")` replaces reading raw `Response`
 *     bodies — and the 30 places that today parse `{error, errors}` by hand.
 *  3. **Auth.** Cookie mode (`credentials: "include"` + `X-Theta-Client`) or
 *     bearer mode, chosen at construction. One client, two consumers: the web
 *     app has a cookie it cannot read, the Expo app has a token it must send.
 *  4. **`Idempotency-Key`.** Generated automatically on creating POSTs (D13),
 *     overridable so a retry can reuse the same key deliberately.
 *  5. **`ifChanged` plumbing.** A pollable read returns
 *     `{unchanged: true, state}` or `{unchanged: false, data, state}` —
 *     `useLivePolling` stores the token and hands it back; it never compares
 *     documents itself.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: caching, retries, a normalised store, or
 * React hooks. `orval` and `@hey-api/openapi-ts` were rejected (§3.4) because
 * they couple generated output to a react-query major, and the Expo app may
 * not use react-query. The north star of the port is that `next-app/lib`
 * SHRINKS; a client that grows its own framework misses it.
 */

export type { components, paths } from "./schema";
export type { EntityId } from "./schema";

type Schemas = components["schemas"];

export type PlatformMeta = Schemas["PlatformMeta"];
export type Me = Schemas["Me"];
export type Avatar = Schemas["Avatar"];
export type ResponseMeta = Schemas["ResponseMeta"];
export type ErrorField = Schemas["ErrorField"];

/** The group page's types (§4.2). Named from the document, never hand-written. */
export type Group = Schemas["Group"];
export type GroupSummary = Schemas["GroupSummary"];
export type GroupCapabilities = Schemas["GroupCapabilities"];
export type ViewerMembership = Schemas["ViewerMembership"];
export type PostCard = Schemas["PostCard"];
/** A rich card for a link pointing back into Theta. Resolved per viewer. */
export type LinkPreview = Schemas["LinkPreview"];
export type PostCapabilities = Schemas["PostCapabilities"];
export type Attachment = Schemas["Attachment"];
export type ReactionSummary = Schemas["ReactionSummary"];
export type Poll = Schemas["Poll"];
export type PostReactions = Schemas["PostReactions"];
/** OSO-622: `POST /posts/read` answers per-id, `POST /notifications/read`-shaped. */
export type MarkPostsReadResult = Schemas["MarkPostsReadResult"];
export type CreatePostInput = Schemas["CreatePostInput"];
export type UserSummary = Schemas["UserSummary"];
export type GroupBot = Schemas["GroupBot"];
export type GroupBotTrace = Schemas["GroupBotTrace"];

/**
 * The profile page's types (§2.2 #12). `UserProfile` has NO `username` — ruling
 * O-21 deleted it from the wire, so `/profil/{id}` is the only profile address
 * there is and there is nothing to resolve on the way in.
 */
export type UserProfile = Schemas["UserProfile"];
export type UserProfileCapabilities = Schemas["UserProfileCapabilities"];
export type ProfileGroup = Schemas["ProfileGroup"];
export type ProfileMembership = Schemas["ProfileMembership"];
export type UserMessaging = Schemas["UserMessaging"];

/**
 * The content domain's types. The ten hand-written methods below name what
 * they return, but the content routes are read through `client.http`, so the
 * shapes they resolve to need names of their own — `Post`, `Comment`,
 * `Upload`, `Document`, `CustomEmoji` and the rest.
 *
 * Aliases, not shapes: rename a field in `backend/src/schemas/content.ts` and,
 * once the document and this package are regenerated, every component that
 * reads it stops compiling.
 */
export type Post = Schemas["Post"];
export type PostComment = Schemas["PostComment"];
export type Comment = Schemas["Comment"];
export type CommentCapabilities = Schemas["CommentCapabilities"];
export type FeedEntry = Schemas["FeedEntry"];
export type Reactor = Schemas["Reactor"];
export type EmojiUse = Schemas["EmojiUse"];
export type Upload = Schemas["Upload"];
export type UploadScope = Schemas["UploadScope"];
export type VideoUploadTicket = Schemas["VideoUploadTicket"];
export type Renditions = Schemas["Renditions"];
/** `Document` is a DOM global; the alias keeps the two apart at a glance. */
export type ThetaDocument = Schemas["Document"];
/** `Document` plus the group it lives in — `GET /documents/{id}` (OSO-612). */
export type ThetaDocumentDetail = Schemas["DocumentDetail"];
export type DocumentFolder = Schemas["DocumentFolder"];
export type CustomEmoji = Schemas["CustomEmoji"];
export type ShortcodeAvailability = Schemas["ShortcodeAvailability"];
export type UpdatePostInput = Schemas["UpdatePostInput"];
export type CreateCommentInput = Schemas["CreateCommentInput"];
export type PollDefinitionInput = Schemas["PollDefinitionInput"];
export type GroupAttachment = Schemas["GroupAttachment"];

/** The named sorts a group feed accepts. Not composed — server-owned (§1.4). */
export type GroupFeedSort = "newest" | "recent_comments" | "popular";

/**
 * A group is addressed by its numeric id OR its slug in the path (D8), so the
 * client takes the same union and the caller never resolves one to the other.
 * That is the type-level half of what deletes `useGroupBySlug`.
 */
export type GroupRef = Group["id"] | string;

/**
 * The error codes the server knows about today, WIDENED so a client compiles
 * against a server that has learned a new one (D15). The union gives
 * autocomplete; the `(string & {})` arm is what stops a new code from being a
 * type error in a shipped binary.
 */
export type ProblemCode = Schemas["ErrorDetail"]["code"] | (string & {});

/** Every non-2xx, in one type. */
export class ThetaError extends Error {
  readonly code: ProblemCode;
  readonly status: number;
  readonly fields: ErrorField[];
  readonly requestId: string | null;
  readonly retryAfter: number | null;

  constructor(init: {
    code: ProblemCode;
    message: string;
    status: number;
    fields?: ErrorField[] | null;
    requestId?: string | null;
    retryAfter?: number | null;
  }) {
    super(init.message);
    this.name = "ThetaError";
    this.code = init.code;
    this.status = init.status;
    this.fields = init.fields ?? [];
    this.requestId = init.requestId ?? null;
    this.retryAfter = init.retryAfter ?? null;
  }
}

/** `isThetaError(e, "membershipRequired")` — the narrowing every screen wants. */
export function isThetaError(error: unknown, code?: ProblemCode): error is ThetaError {
  return error instanceof ThetaError && (code === undefined || error.code === code);
}

/** One page of a collection, in the shape an infinite query consumes. */
export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasMore: boolean;
}

/**
 * The answer to a pollable read (§1.10). `unchanged` is the discriminant, so a
 * caller cannot read `data` off a response that does not carry one.
 */
export type Pollable<T> =
  | { unchanged: true; state: string; pollAfterMs: number | null }
  | { unchanged: false; data: T; state: string | null; pollAfterMs: number | null };

export interface ThetaClientOptions {
  /** Defaults to `/api/v1` — the same-origin base the web app uses (D1). */
  baseUrl?: string;
  /**
   * `cookie` for the browser: the session rides in an HttpOnly cookie the
   * client cannot read, so mutations must carry `X-Theta-Client` and an
   * allow-listed `Origin` instead of a CSRF token (D6).
   * `bearer` for Expo: the same `sessions` row, carried in a header, exempt
   * from the client-header rule because a cross-site form cannot send one.
   */
  auth?:
    | { mode: "cookie" }
    | { mode: "bearer"; token: () => string | null | undefined };
  /** `name/version`, e.g. `theta-web/0.2.0`. Sent on every request. */
  clientId: string;
  /** Injectable for tests and for React Native. */
  fetch?: typeof globalThis.fetch;
}

/** POSTs that create something get an `Idempotency-Key` whether or not you ask (D13). */
const IDEMPOTENT_METHODS = new Set(["POST"]);

export function createThetaClient(options: ThetaClientOptions) {
  const auth = options.auth ?? { mode: "cookie" as const };

  const http = createOpenApiClient<paths>({
    baseUrl: options.baseUrl ?? "/api/v1",
    ...(options.fetch ? { fetch: options.fetch } : {}),
    // Never on a cross-origin request: `api.theta.dk` answers Expo without
    // `Access-Control-Allow-Credentials`, so a cookie can never ride one
    // (§1.6). In cookie mode the API is same-origin and this is what sends it.
    ...(auth.mode === "cookie" ? { credentials: "include" as const } : {}),
  });

  const headers: Middleware = {
    onRequest({ request }) {
      request.headers.set("X-Theta-Client", options.clientId);
      if (auth.mode === "bearer") {
        const token = auth.token();
        if (token) request.headers.set("Authorization", `Bearer ${token}`);
      }
      if (IDEMPOTENT_METHODS.has(request.method) && !request.headers.has("Idempotency-Key")) {
        request.headers.set("Idempotency-Key", newIdempotencyKey());
      }
      return request;
    },
  };
  http.use(headers);

  return {
    /** The raw typed transport, for a call this client has no method for yet. */
    http,

    /** Platform constants (§2.1). Anonymous, cacheable, changes on deploy. */
    async meta(): Promise<PlatformMeta> {
      return unwrap<Envelope<PlatformMeta>>(await http.GET("/meta", {})).data;
    },

    /**
     * The signed-in user, or `null` when signed out — never an error (§1.6).
     * Pass the previous `state` to poll cheaply.
     */
    async me(init: { ifChanged?: string } = {}): Promise<Pollable<Me | null>> {
      return pollable(
        unwrap<Envelope<Me | null>>(
          await http.GET("/me", {
            params: { query: init.ifChanged ? { ifChanged: init.ifChanged } : {} },
          }),
        ),
      );
    },

    /* ----------------------------------------------- users and profiles */

    /**
     * One person's profile, BY ID — the only way to address one (ruling O-21a).
     *
     * There is no by-username form and there must never be one: ten accounts in
     * the corpus carry a numeric username equal to a DIFFERENT user's id, and
     * all ten are real Theta accounts, so a route that resolved "either" would
     * serve the wrong person's profile silently. `id` is the branded
     * `EntityId<"user">`, so a group id cannot be passed here by accident.
     *
     * `inGroup` makes the answer CONTEXTUAL — `membershipInGroup` and
     * `affiliation` are populated from that group, and it takes a `{ref}` (id
     * or slug), exactly as a group path segment does (D8).
     *
     * THROWS `unauthenticated` without a session (OSO-695), and `notFound`
     * for a missing id, a deleted row or a staged invite placeholder —
     * deliberately the same answer for all three (D7), so the route cannot be
     * walked to tell them apart.
     */
    async user(
      id: UserProfile["id"] | number,
      init: { inGroup?: GroupRef } = {},
    ): Promise<UserProfile> {
      return unwrap<Envelope<UserProfile>>(
        await http.GET("/users/{id}", {
          params: {
            path: { id: Number(id) },
            query: init.inGroup !== undefined ? { inGroup: String(init.inGroup) } : {},
          },
        }),
      ).data;
    },

    /**
     * "May I write to this person, and does the thread already exist?" — the
     * profile page's "Send besked" button, by id.
     *
     * `reason` is `self` or `no_shared_group` and is NEVER `blocked`: a blocked
     * sender gets a 201 and a silently declined participant row precisely so
     * the block is not confirmed to the person it was placed against, and
     * reporting it here would undo that in one field.
     *
     * `canMessage` is the same verdict `UserProfile.can.message` carries, so a
     * screen that already has the profile does not need this call to decide
     * whether to DRAW the button — only to decide where it goes.
     */
    async userMessaging(id: UserProfile["id"] | number): Promise<UserMessaging> {
      return unwrap<Envelope<UserMessaging>>(
        await http.GET("/users/{id}/messaging", {
          params: { path: { id: Number(id) } },
        }),
      ).data;
    },

    /* ------------------------------------------------------ the group page */

    /**
     * One group, by id OR by slug (D8).
     *
     * The EXISTENCE rule: a private group answers here and 404s on its posts,
     * a secret group 404s throughout. So a caller renders the shell and lets
     * `groupPosts()` throw a `notFound` rather than pre-checking privacy —
     * which is what stops the client from carrying its own copy of the rule.
     */
    async group(ref: GroupRef): Promise<Group> {
      return unwrap<Envelope<Group>>(
        await http.GET("/groups/{ref}", { params: { path: { ref: String(ref) } } }),
      ).data;
    },

    /**
     * One page of a group's feed.
     *
     * Pollable: pass the previous `state` as `ifChanged` and receive
     * `{unchanged: true}` while nothing the page renders has changed. The
     * discriminated `Pollable` is what stops a caller reading `.items` off a
     * response that has none.
     */
    async groupPosts(
      ref: GroupRef,
      init: {
        sort?: GroupFeedSort;
        cursor?: string | null;
        limit?: number;
        ifChanged?: string;
      } = {},
    ): Promise<Pollable<Page<PostCard>>> {
      const envelope = unwrap<Envelope<PostCard[] | null>>(
        await http.GET("/groups/{ref}/posts", {
          params: {
            path: { ref: String(ref) },
            query: {
              ...(init.sort ? { sort: init.sort } : {}),
              ...(init.cursor ? { cursor: init.cursor } : {}),
              ...(init.limit ? { limit: init.limit } : {}),
              ...(init.ifChanged ? { ifChanged: init.ifChanged } : {}),
            },
          },
        }),
      );

      const pollAfterMs = envelope.meta.pollAfterMs ?? null;
      if (envelope.meta.unchanged) {
        return { unchanged: true, state: envelope.meta.state ?? "", pollAfterMs };
      }
      return {
        unchanged: false,
        data: toPage({ ...envelope, data: envelope.data ?? [] }),
        state: envelope.meta.state ?? null,
        pollAfterMs,
      };
    },

    /**
     * Write a post. `Idempotency-Key` is generated by the middleware above and
     * is REQUIRED by the server (D13) — pass `idempotencyKey` only to REPLAY a
     * request deliberately, which is what a retry queue does after a timeout.
     *
     * `attachments` are upload ids, in display order. There are no markdown
     * tokens in either direction (§1.12).
     */
    async createPost(
      ref: GroupRef,
      body: CreatePostInput,
      init: { idempotencyKey?: string } = {},
    ): Promise<PostCard> {
      return unwrap<Envelope<PostCard>>(
        await http.POST("/groups/{ref}/posts", {
          params: { path: { ref: String(ref) } },
          body,
          ...(init.idempotencyKey
            ? { headers: { "Idempotency-Key": init.idempotencyKey } }
            : {}),
        }),
      ).data;
    },

    /**
     * Set or swap the viewer's reaction. A singleton, so calling it twice with
     * the same emoji is one row.
     *
     * The response is the COMPLETE reaction summary. Splice it into the card
     * you already have; do not recompute the swap and do not invalidate the
     * feed — that recomputation is 22 lines of `usePostReaction` this replaces.
     */
    /**
     * A rich card for a link that points back into Theta.
     *
     * Returns `null` when the URL is not a Theta link — the caller fetches
     * external metadata itself. THROWS `notFound` when the target exists but
     * this viewer may not see it, which is deliberately the same answer as a
     * target that does not exist: a pasted link must not become a way to ask
     * whether a secret group is real. Callers should render a plain link on
     * either, and must not tell the user which one they got.
     */
    async linkPreview(url: string): Promise<LinkPreview | null> {
      return unwrap<Envelope<LinkPreview | null>>(
        await http.GET("/link-previews", { params: { query: { url } } }),
      ).data;
    },

    async setReaction(postId: PostCard["id"], emoji: string): Promise<PostReactions> {
      return unwrap<Envelope<PostReactions>>(
        await http.PUT("/posts/{id}/reaction", {
          params: { path: { id: postId } },
          body: { emoji },
        }),
      ).data;
    },

    /** Remove it. 200 even when there was nothing to remove (QR8). */
    async removeReaction(postId: PostCard["id"]): Promise<PostReactions> {
      return unwrap<Envelope<PostReactions>>(
        await http.DELETE("/posts/{id}/reaction", {
          params: { path: { id: postId } },
        }),
      ).data;
    },
  };
}

export type ThetaClient = ReturnType<typeof createThetaClient>;

// Internals

/**
 * The response body, in terms of the GENERATED component types.
 *
 * WHY THE METHODS NAME IT INSTEAD OF LETTING `openapi-fetch` INFER IT.
 * `openapi-fetch`'s response type runs the body through its `Readable<T>`
 * helper, which strips `readOnly` properties with a homomorphic mapped type —
 * `T extends object ? {[K in keyof T]: …}`. A branded id is
 * `number & {readonly __entity: "user"}`, which IS an object type, so the
 * mapping walks it and hands back `{toFixed: {}, toString: {}, __entity:
 * "user"}` instead of a number. Every brand in the document would be destroyed
 * on the way out.
 *
 * So the REQUEST side stays fully inferred (a path that does not exist, or a
 * query parameter the route does not take, is still a compile error) and the
 * RESPONSE side is named from `components["schemas"]` — the same generated
 * types, one hop earlier, before the helper can flatten them. It is one cast,
 * in one function, and it is what keeps `EntityId<"group">` meaningful in both
 * consumers (§3.4).
 */
interface Envelope<T> {
  data: T;
  page?: { nextCursor: string | null; prevCursor: string | null; hasMore: boolean };
  meta: ResponseMeta;
}

/**
 * The one place a response stops being HTTP and becomes a value or a throw.
 *
 * An error body is validated loosely on purpose: if the server ever answers a
 * non-2xx that is NOT in the contract shape (a proxy's HTML 502, say), the
 * client must still produce a `ThetaError` rather than a `TypeError` from
 * reading `.error.code` of undefined.
 */
function unwrap<T>(result: {
  data?: unknown;
  error?: unknown;
  response: Response;
}): T {
  const { data, error, response } = result;
  if (error !== undefined || !response.ok || data === undefined) {
    const body = (error ?? {}) as {
      error?: { code?: string; message?: string; fields?: ErrorField[] | null; retryAfter?: number | null };
      meta?: { requestId?: string };
    };
    throw new ThetaError({
      code: body.error?.code ?? "internalError",
      message: body.error?.message ?? `HTTP ${response.status}`,
      status: response.status,
      fields: body.error?.fields ?? [],
      requestId: body.meta?.requestId ?? null,
      retryAfter: body.error?.retryAfter ?? null,
    });
  }
  return data as T;
}

function pollable<T>(envelope: Envelope<T>): Pollable<T> {
  const pollAfterMs = envelope.meta.pollAfterMs ?? null;
  if (envelope.meta.unchanged) {
    return { unchanged: true, state: envelope.meta.state ?? "", pollAfterMs };
  }
  return {
    unchanged: false,
    data: envelope.data,
    state: envelope.meta.state ?? null,
    pollAfterMs,
  };
}

/** A collection envelope -> the page shape. Used by every list method. */
export function toPage<T>(envelope: Envelope<T[]>): Page<T> {
  return {
    items: envelope.data,
    nextCursor: envelope.page?.nextCursor ?? null,
    prevCursor: envelope.page?.prevCursor ?? null,
    hasMore: envelope.page?.hasMore ?? false,
  };
}

/**
 * Walks every page of a collection.
 *
 * `for await (const group of paginate(c => client.groups({cursor: c})))` — the
 * cursor never leaves this function, which is what stops a caller from
 * inventing one or from feeding a cursor back under a different filter set
 * (`400 cursorMismatch`, §1.3).
 */
export async function* paginate<T>(
  fetchPage: (cursor: string | null) => Promise<Page<T>>,
): AsyncGenerator<T, void, undefined> {
  let cursor: string | null = null;
  for (;;) {
    const page: Page<T> = await fetchPage(cursor);
    for (const item of page.items) yield item;
    if (!page.hasMore || !page.nextCursor) return;
    cursor = page.nextCursor;
  }
}

/**
 * `crypto.randomUUID` where it exists (every browser we support, Node 22, and
 * Hermes with the polyfill Expo ships); a random fallback otherwise, because
 * an idempotency key only has to be unique, not unguessable.
 */
function newIdempotencyKey(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
